import React from "react";
import type { Change } from "../../../../utils/fleetDerivations";
import { DASH, formatSignedNumber, formatSignedPercent } from "../../../../utils/fleetDerivations";

interface StatTileProps {
  label: string;
  value: string;
  /** The change against the previous period, when one applies. */
  change?: Change;
  /** "vs August 2026" — every change names what it compares against (FR-038). */
  comparedTo?: string;
  /** Formats the absolute change in the tile's own unit. */
  formatAbsolute?: (value: number | null) => string;
  hint?: string;
}

/**
 * BOF-76 — one headline figure with its change.
 *
 * The change is written out with its sign ("+3.2%") and its absolute value, so direction is never carried
 * by colour alone. Against a zero or absent previous value the percentage is "—" while the absolute change
 * still shows (FR-039). Colour is a secondary cue only, and it is not a verdict: more is not always better.
 */
const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  change,
  comparedTo,
  formatAbsolute = (v) => formatSignedNumber(v),
  hint,
}) => {
  const direction = change?.absolute == null ? 0 : Math.sign(change.absolute);
  return (
    <div className="flex flex-column gap-1" title={hint}>
      <span className="text-sm text-color-secondary">{label}</span>
      <span className="text-2xl font-semibold text-900" data-testid={`tile-${label}`}>
        {value}
      </span>
      {change && (
        <span className="text-sm text-color-secondary">
          <i
            className={`pi ${direction > 0 ? "pi-arrow-up-right" : direction < 0 ? "pi-arrow-down-right" : "pi-minus"} mr-1`}
            style={{ fontSize: "0.75rem" }}
            aria-hidden
          />
          {change.percent == null ? DASH : formatSignedPercent(change.percent)}
          {" · "}
          {formatAbsolute(change.absolute)}
          {comparedTo && <span className="ml-1">{comparedTo}</span>}
        </span>
      )}
    </div>
  );
};

export default StatTile;
